import React from 'react';
import type { UnifiedRxSearchResult } from '../../api';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { formatStandardDate } from 'shared-utils/utils/formatDate';
import { SearchHighlightText } from '../SearchHighlightText/SearchHighlightText';
import { PreviewDateWithStatus } from '../Table';

interface RxPreviewItemProps {
  rx: UnifiedRxSearchResult;
  searchTerm: string;
}

/**
 * Preview item component for prescription search results.
 * Highlights fields that match the search term (drug, patient name, DOB).
 * Supports both regular prescriptions and processing prescriptions.
 */
export const RxPreviewItem: React.FC<RxPreviewItemProps> = ({ rx, searchTerm }) => {
  const formattedDob = rx.patient_dob ? formatStandardDate(rx.patient_dob) : '';

  const isClosed = rx.portal_status === 'closed';

  return (
    <div className="flex items-start justify-between gap-2">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {/* Drug name row */}
        <div className="flex items-baseline gap-1">
          <Typography
            styledAs="bodySmallDMSans"
            renderedAs="div"
            weight={600}
            color="var(--grayscale-900)"
            className="max-w-[150px] shrink-0"
          >
            <SearchHighlightText searchTerm={searchTerm} value={rx.drug_name || ''} fieldType="text" truncate />
          </Typography>
          {rx.drug_description && rx.drug_description !== rx.drug_name && (
            <>
              <Typography styledAs="bodyExtraSmallDMSans" renderedAs="span" color="var(--grayscale-600)" aria-hidden>
                |
              </Typography>
              <Typography styledAs="bodyExtraSmallDMSans" renderedAs="div" className="max-w-[165px] shrink-0">
                <SearchHighlightText searchTerm={searchTerm} value={rx.drug_description} fieldType="text" truncate />
              </Typography>
            </>
          )}
        </div>

        {/* Patient info row */}
        <div className="flex items-baseline gap-1">
          <Typography
            styledAs="bodyExtraSmallDMSans"
            renderedAs="div"
            weight={500}
            color="var(--grayscale-900)"
            className="max-w-[200px] shrink-0"
          >
            <SearchHighlightText searchTerm={searchTerm} value={rx.patient_name || ''} fieldType="name" truncate />
          </Typography>
          {formattedDob && (
            <Typography styledAs="bodyExtraSmallDMSans" renderedAs="span" color="var(--grayscale-600)">
              (DOB: <SearchHighlightText searchTerm={searchTerm} value={formattedDob} fieldType="date" />)
            </Typography>
          )}
        </div>
      </div>

      <PreviewDateWithStatus date={rx.created_at} statusLabel={isClosed ? 'Closed' : undefined} />
    </div>
  );
};

export default RxPreviewItem;
